import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { api } from "../../api";
import { ADMIN_PATH } from "../../config";

const slugify = (str: string) =>
    str.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

const wrapSelection = (
    textarea: HTMLTextAreaElement,
    before: string,
    after: string,
    placeholder: string,
    setter: (v: string) => void
) => {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = textarea.value.slice(start, end) || placeholder;
    const newValue =
        textarea.value.slice(0, start) + before + selected + after + textarea.value.slice(end);
    setter(newValue);
    setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
};

const Editor = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditing = Boolean(id);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [content, setContent] = useState("");
    const [excerpt, setExcerpt] = useState("");
    const [tags, setTags] = useState("");
    const [published, setPublished] = useState(false);
    const [preview, setPreview] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isEditing) {
            api.getAllPosts().then((posts: any[]) => {
                const post = posts.find((p: any) => p._id === id);
                if (post) {
                    setTitle(post.title);
                    setSlug(post.slug);
                    setContent(post.content);
                    setExcerpt(post.excerpt ?? "");
                    setTags(post.tags?.join(", ") ?? "");
                    setPublished(post.published);
                }
            });
        }
    }, [id]);

    const handleTitleChange = (val: string) => {
        setTitle(val);
        if (!isEditing) setSlug(slugify(val));
    };

    const handleSave = async (publish: boolean) => {
        setSaving(true);
        const data = {
            title,
            slug,
            content,
            excerpt,
            tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
            published: publish,
        };
        if (isEditing) {
            await api.updatePost(id!, data);
        } else {
            await api.createPost(data);
        }
        setSaving(false);
        navigate(ADMIN_PATH);
    };

    const fmt = (before: string, after: string, placeholder: string) => {
        if (textareaRef.current) wrapSelection(textareaRef.current, before, after, placeholder, setContent);
    };

    const insertLink = () => {
        const url = window.prompt("URL:");
        if (!url) return;
        if (textareaRef.current) wrapSelection(textareaRef.current, "[", `](${url})`, "link text", setContent);
    };

    const insertImage = () => {
        const url = window.prompt("Image URL:");
        if (!url) return;
        const alt = window.prompt("Alt text (optional):") ?? "";
        if (textareaRef.current) {
            const ta = textareaRef.current;
            const pos = ta.selectionStart;
            const insert = `![${alt}](${url})`;
            const newValue = ta.value.slice(0, pos) + insert + ta.value.slice(pos);
            setContent(newValue);
            setTimeout(() => { ta.focus(); ta.setSelectionRange(pos + insert.length, pos + insert.length); }, 0);
        }
    };

    const toolbarBtn = (label: string, onClick: () => void, style?: string) => (
        <button
            type="button"
            onClick={onClick}
            className={`font-inter text-xs px-2 py-1 rounded text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 transition-colors cursor-pointer ${style ?? ""}`}
        >
            {label}
        </button>
    );

    return (
        <div className="min-h-screen bg-(--bg-main) px-8 py-10 max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <button
                    onClick={() => navigate(ADMIN_PATH)}
                    className="font-inter text-sm text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
                >
                    ← dashboard
                </button>
                <div className="flex gap-3 items-center">
                    <button
                        onClick={() => setPreview(!preview)}
                        className="font-inter text-sm text-neutral-500 hover:text-[#F4611D] transition-colors cursor-pointer"
                    >
                        {preview ? "edit" : "preview"}
                    </button>
                    <button
                        onClick={() => handleSave(false)}
                        disabled={saving}
                        className="font-inter text-sm px-4 py-2 rounded-full border border-neutral-300 text-neutral-500 hover:border-neutral-400 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                        save draft
                    </button>
                    <button
                        onClick={() => handleSave(true)}
                        disabled={saving}
                        className="font-inter text-sm px-4 py-2 rounded-full bg-[#F4611D] text-white hover:bg-[#d94e0f] transition-colors disabled:opacity-50 cursor-pointer"
                    >
                        {published ? "update" : "publish"}
                    </button>
                </div>
            </div>

            {preview ? (
                <div className="mt-6">
                    <h1 className="font-is text-5xl">{title || "untitled"}</h1>
                    <div
                        className="prose mt-8 font-inter text-base font-light leading-relaxed text-neutral-700"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(content) as string) }}
                    />
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <input
                        type="text"
                        placeholder="title"
                        value={title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        className="font-is text-4xl bg-transparent outline-none placeholder:text-neutral-300 border-b border-neutral-200 pb-3"
                    />
                    <input
                        type="text"
                        placeholder="slug"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        className="font-inter text-xs text-neutral-400 bg-transparent outline-none placeholder:text-neutral-300"
                    />
                    <input
                        type="text"
                        placeholder="excerpt (optional)"
                        value={excerpt}
                        onChange={(e) => setExcerpt(e.target.value)}
                        className="font-inter text-sm bg-transparent outline-none placeholder:text-neutral-300 border-b border-neutral-200 pb-3"
                    />
                    <input
                        type="text"
                        placeholder="tags (comma separated)"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        className="font-inter text-sm bg-transparent outline-none placeholder:text-neutral-300 border-b border-neutral-200 pb-3"
                    />

                    <div className="flex items-center gap-1 border border-neutral-200 rounded-lg px-2 py-1 w-fit mt-2">
                        {toolbarBtn("B", () => fmt("**", "**", "bold text"), "font-bold")}
                        {toolbarBtn("I", () => fmt("*", "*", "italic text"), "italic")}
                        {toolbarBtn("U", () => fmt("<u>", "</u>", "underlined text"))}
                        <div className="w-px h-4 bg-neutral-200 mx-1" />
                        {toolbarBtn("H1", () => fmt("# ", "", "Heading 1"))}
                        {toolbarBtn("H2", () => fmt("## ", "", "Heading 2"))}
                        <div className="w-px h-4 bg-neutral-200 mx-1" />
                        {toolbarBtn("Link", insertLink)}
                        {toolbarBtn("Image", insertImage)}
                        <div className="w-px h-4 bg-neutral-200 mx-1" />
                        {toolbarBtn('""', () => fmt("> ", "", "quoted text"))}
                        {toolbarBtn("</>", () => fmt("`", "`", "code"))}
                    </div>

                    <textarea
                        ref={textareaRef}
                        placeholder="write in markdown..."
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows={24}
                        className="font-inter text-sm bg-transparent outline-none resize-none placeholder:text-neutral-300 leading-relaxed"
                    />
                </div>
            )}
        </div>
    );
};

export default Editor;
